// components/TaxBasisCard.jsx — the engagement's tax basis (kailux-fixes-log.md §108).
//
// "Standard company rate" or "Small business corporation (SBC)", confirmed by the preparer for THIS
// engagement and recorded with their name, the time and the SBC table relied on. Later features
// (the validation gate, the PDF tax note, deferred tax) read this one record, so SBC clients who
// book their own tax in the trial balance are handled too, not only ones who add a provision here.
//
// KaiLux never assumes a basis and never decides eligibility: NEITHER option is ever pre-selected
// (the client-level "last confirmed as an SBC for FY…" hint only reminds), SBC needs the explicit
// confirmation tick every year, and a year-end with no verified table cannot use SBC at all — the
// card says why and links to the admin screen where a table can be added. Standard is always available.
import { useState, useEffect } from 'react'
import { getTaxBasis, saveTaxBasis, saveDeferredTaxRate } from '../api/kailux'
import { basisLabel } from '../utils/taxProvision'
import SbcConfirmation from './SbcConfirmation'
import { T, alpha } from '../theme/theme'

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('en-ZA') } catch { return '' } }

export default function TaxBasisCard({ clientId, financialYear, yearEnd, refreshKey = 0, onChanged }) {
  const [data, setData]           = useState(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [changing, setChanging]   = useState(false)
  const [picked, setPicked]       = useState(null)       // null | 'sbc'  (standard saves on click)
  const [confirmed, setConfirmed] = useState(false)
  const [busy, setBusy]           = useState(false)
  const [error, setError]         = useState('')
  // §117: the deferred-tax rate basis (SBC only). Nothing is preselected and nothing is assumed silently.
  const [dtMode, setDtMode]       = useState(null)   // null | 'enter'
  const [dtPercent, setDtPercent] = useState('')
  const [dtReason, setDtReason]   = useState('')
  const [dtError, setDtError]     = useState('')

  useEffect(() => {
    if (!clientId || !financialYear) return undefined
    let cancelled = false
    setLoadFailed(false)
    getTaxBasis(clientId, financialYear, yearEnd || '')
      .then((d) => { if (!cancelled) setData(d) })
      .catch(() => { if (!cancelled) { setData(null); setLoadFailed(true) } })
    return () => { cancelled = true }
  }, [clientId, financialYear, yearEnd, refreshKey])

  if (loadFailed) {
    // Never block the preparer: the provision card and the validation gate still do their jobs.
    return <div data-testid="tax-basis-unavailable" style={{ fontSize: '12px', color: T.mt, marginBottom: '12px' }}>
      The tax basis for this engagement could not be loaded just now.
    </div>
  }
  if (!data) return null

  const current   = data.basis
  const sbc       = data.sbc || {}
  const showChoice = !current || changing
  const btn = (primary, disabled) => ({ padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
    cursor: busy || disabled ? 'default' : 'pointer', border: primary ? 'none' : `1px solid ${T.bd}`,
    background: primary ? T.prg : 'transparent', color: primary ? T.onpri : T.lt, opacity: busy || disabled ? 0.6 : 1 })

  const save = async (basis) => {
    setBusy(true); setError('')
    try {
      const rec = await saveTaxBasis({ clientId, financialYear, basis, sbcConfirmed: basis === 'sbc' && confirmed, yearEnd: yearEnd || '' })
      setData((d) => ({ ...d, basis: rec.basis ? { basis: rec.basis, by: rec.by, at: rec.at, table_label: rec.table_label } : null }))
      setPicked(null); setConfirmed(false); setChanging(false)
      if (onChanged) onChanged(rec)
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not save the tax basis. Please try again.')
    } finally { setBusy(false) }
  }

  const statPct   = data.statutory_rate != null ? Number((data.statutory_rate * 100).toFixed(4)) : null
  const dr        = data.deferred_rate

  const saveDt = async (basis) => {
    setBusy(true); setDtError('')
    try {
      let rate
      if (basis === 'entered') {
        const pct = Number(dtPercent)
        if (dtPercent.trim() === '' || !Number.isFinite(pct)) { setDtError('Enter the effective rate as a percentage, for example 15.'); return }
        if (pct < 0) { setDtError('The effective rate cannot be negative.'); return }
        if (statPct != null && pct > statPct) { setDtError(`The effective rate cannot be higher than the statutory rate (${statPct}%).`); return }
        rate = Number((pct / 100).toFixed(6))
      }
      const rec = await saveDeferredTaxRate({ clientId, financialYear, basis, rate, reason: basis === 'entered' ? dtReason : '', yearEnd: yearEnd || '' })
      setData((d) => ({ ...d, deferred_rate: rec.basis ? { basis: rec.basis, rate: rec.rate, reason: rec.reason, by: rec.by, at: rec.at } : null }))
      setDtMode(null); setDtPercent(''); setDtReason('')
      if (onChanged) onChanged(rec)
    } catch (err) {
      setDtError(err?.response?.data?.detail || 'Could not save the deferred tax rate. Please try again.')
    } finally { setBusy(false) }
  }

  const box = { background: current && !changing ? alpha(T.gn, 0.06) : T.s2, border: `1px solid ${current && !changing ? alpha(T.gn, 0.3) : T.bd}`,
    borderRadius: '12px', padding: '14px 18px', marginBottom: '16px' }

  return (
    <div style={box} data-testid="tax-basis-card">
      <div style={{ fontSize: '12px', fontWeight: '700', letterSpacing: '1.5px', marginBottom: '8px', color: current ? T.gn : T.yl }}>
        TAX BASIS FOR THIS ENGAGEMENT{current ? '' : ' — NOT YET CONFIRMED'}
      </div>

      {current && !changing && (
        <div data-testid="tax-basis-resolved" style={{ fontSize: '14px', color: T.tx, lineHeight: 1.55, marginBottom: '10px' }}>
          Confirmed as the <strong>{basisLabel(current.basis)}</strong> basis
          {current.basis === 'sbc' && current.table_label ? ` (${current.table_label})` : ''}
          {current.by ? ` by ${current.by}` : ''}{current.at ? ` on ${fmtDate(current.at)}` : ''}.
          {current.basis === 'sbc' && <> You confirmed the company meets the small business corporation conditions for this year of assessment.</>}
        </div>
      )}

      {showChoice && (
        <>
          <div style={{ fontSize: '13px', color: T.lt, lineHeight: 1.5, marginBottom: '10px' }}>
            Choose how this company's income tax is worked out. KaiLux does not assume either — it records your choice.
          </div>
          {data.hint?.financial_year && !picked && (
            <div data-testid="sbc-hint" role="note" style={{ fontSize: '12px', color: T.yl, marginBottom: '10px' }}>
              Last confirmed as a small business corporation for FY{data.hint.financial_year}. That does not carry over — eligibility
              is checked for every year of assessment.
            </div>
          )}
          {!picked && (
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button type="button" style={btn(false)} disabled={busy} onClick={() => save('standard')}>Use the standard company rate</button>
              <button type="button" style={btn(false, !sbc.available)} disabled={busy || !sbc.available}
                onClick={() => { setPicked('sbc'); setConfirmed(false) }}>Small business corporation…</button>
              {changing && <button type="button" style={btn(false)} disabled={busy} onClick={() => setChanging(false)}>Keep my answer</button>}
            </div>
          )}
          {!sbc.available && !picked && (
            <div data-testid="sbc-unavailable" style={{ fontSize: '12px', color: T.lt, marginTop: '10px', maxWidth: '620px', lineHeight: 1.5 }}>
              {sbc.unavailable_reason}{' '}
              {sbc.admin_path && <a href={sbc.admin_path} style={{ color: T.prg }}>Open the admin screen</a>}
            </div>
          )}
          {picked === 'sbc' && (
            <div>
              <div style={{ fontSize: '13px', color: T.tx }}>
                Small business corporation basis{sbc.table_label ? ` — ${sbc.table_label}` : ''}
              </div>
              <SbcConfirmation conditions={sbc.conditions || []} confirmed={confirmed} onChange={setConfirmed} hint={data.hint} idPrefix="basis-card" />
              <div style={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap' }}>
                <button type="button" style={btn(true, !confirmed)} disabled={busy || !confirmed} onClick={() => save('sbc')}>Confirm small business corporation basis</button>
                <button type="button" style={btn(false)} disabled={busy} onClick={() => { setPicked(null); setConfirmed(false) }}>Cancel</button>
              </div>
            </div>
          )}
        </>
      )}

      {current && !changing && (
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button type="button" style={btn(false)} disabled={busy} onClick={() => setChanging(true)}>Change the basis</button>
          <button type="button" style={btn(false)} disabled={busy} onClick={() => save('cleared')}>Withdraw my confirmation</button>
        </div>
      )}

      {current && !changing && current.basis === 'sbc' && (
        <div data-testid="deferred-rate-section" style={{ marginTop: '14px', paddingTop: '12px', borderTop: `1px solid ${T.bd}` }}>
          <div style={{ fontSize: '12px', fontWeight: '700', letterSpacing: '1.5px', marginBottom: '6px', color: dr ? T.gn : T.yl }}>
            DEFERRED TAX RATE{dr ? '' : ' — NOT YET CHOSEN'}
          </div>
          {dr ? (
            <div data-testid="deferred-rate-resolved" style={{ fontSize: '13px', color: T.tx, lineHeight: 1.55, marginBottom: '8px' }}>
              {dr.basis === 'statutory'
                ? <>Deferred tax is measured at the <strong>statutory rate{statPct != null ? ` (${statPct}%)` : ''}</strong>.</>
                : <>Deferred tax is measured at an effective rate of <strong>{Number((dr.rate * 100).toFixed(4))}%</strong>
                    {statPct != null ? ` (the statutory rate is ${statPct}%)` : ''}{dr.reason ? ` — ${dr.reason}` : ''}.</>}
              {dr.by ? ` Chosen by ${dr.by}.` : ''} This is disclosed in the income tax note.
            </div>
          ) : (
            <div data-testid="deferred-rate-unchosen" style={{ fontSize: '13px', color: T.lt, lineHeight: 1.5, marginBottom: '8px', maxWidth: '640px' }}>
              For a small business corporation the rate at which temporary differences reverse is a judgement, so KaiLux does not assume it.
              Until you choose, deferred tax is measured at the statutory rate{statPct != null ? ` of ${statPct}%` : ''}.
            </div>
          )}
          {dtMode !== 'enter' && (
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button type="button" style={btn(false)} disabled={busy} onClick={() => saveDt('statutory')}>
                Use the statutory rate{statPct != null ? ` (${statPct}%)` : ''}
              </button>
              <button type="button" style={btn(false)} disabled={busy} onClick={() => { setDtMode('enter'); setDtError('') }}>Enter an effective rate…</button>
              {dr && <button type="button" style={btn(false)} disabled={busy} onClick={() => saveDt('cleared')}>Withdraw my choice</button>}
            </div>
          )}
          {dtMode === 'enter' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '420px' }}>
              <label style={{ fontSize: '12px', color: T.lt }}>Effective rate expected when the differences reverse (%)
                <input type="number" min="0" step="0.1" aria-label="Effective deferred tax rate (%)" value={dtPercent}
                  onChange={(e) => setDtPercent(e.target.value)}
                  style={{ display: 'block', marginTop: '4px', padding: '8px 10px', borderRadius: '8px', border: `1px solid ${T.bd}`, background: T.s2, color: T.tx, width: '140px' }} />
              </label>
              <label style={{ fontSize: '12px', color: T.lt }}>Why this rate? (optional, shown in the tax note)
                <input type="text" aria-label="Reason for the rate" value={dtReason} maxLength={300} onChange={(e) => setDtReason(e.target.value)}
                  style={{ display: 'block', marginTop: '4px', padding: '8px 10px', borderRadius: '8px', border: `1px solid ${T.bd}`, background: T.s2, color: T.tx, width: '100%', boxSizing: 'border-box' }} />
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" style={btn(true)} disabled={busy} onClick={() => saveDt('entered')}>Use this rate</button>
                <button type="button" style={btn(false)} disabled={busy} onClick={() => { setDtMode(null); setDtError('') }}>Cancel</button>
              </div>
            </div>
          )}
          {dtError && <div role="alert" data-testid="deferred-rate-error" style={{ fontSize: '13px', color: T.rd, marginTop: '8px' }}>{dtError}</div>}
        </div>
      )}

      {error && <div role="alert" style={{ fontSize: '13px', color: T.rd, marginTop: '10px' }}>{error}</div>}
    </div>
  )
}
